export const PENDING_LINK_STATUS_LABELS: Record<string, string> = {
  awaiting_review: 'Awaiting review',
  under_review: 'Under review',
  approved_once: 'Approved once',
  approved: 'Approved',
  rejected: 'Rejected',
  blocked: 'Blocked',
};

export const PENDING_LINK_SOURCE_LABELS: Record<string, string> = {
  ui: 'Manual',
  api: 'API',
  import: 'Import',
  bulk: 'Bulk',
  'api-v2': 'API-V2',
  'api-v2.1': 'API-V2.1',
  'api-v3': 'API-V3',
  'api-v4': 'API-V4',
  'api-v5': 'API-V5',
  elink: 'E-Link',
  'power-page': 'Power Page',
};

export type PendingLinkDecisionAction = 'approve' | 'approve_once' | 'reject';

export type PendingLinkBulkDecision = 'approved' | 'approved_once' | 'rejected';

export interface PendingLinkRequester {
  id?: number | string;
  uuid?: string;
  name?: string;
  email?: string;
  role?: string;
}

export interface PendingLinkRequestPayload {
  longUrl?: string;
  submittedBy?: string;
}

export interface PendingLink {
  pendingLinkUuid: string;
  linkId: number | string;
  domainId?: number | string;
  domainUuid: string;
  domainName?: string;
  accountId?: number | string;
  status: string;
  sourceSurface?: string;
  requestPayload?: PendingLinkRequestPayload;
  requester?: PendingLinkRequester;
  validationErrors?: unknown;
  lastDecision?: {
    decision?: string;
    decidedAt?: string;
    reasonCode?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  longUrl?: string;
}

export interface PendingLinksListMeta {
  currentPage: number;
  lastPage?: number;
  perPage: number;
  to?: number;
  total: number;
  totalPages?: number;
}

export interface PendingLinksListResponse {
  data: PendingLink[];
  meta: PendingLinksListMeta;
}

export const DEFAULT_PENDING_LINKS_CURRENT_PAGE = 1;
export const DEFAULT_PENDING_LINKS_PER_PAGE = 25;
export const PENDING_LINKS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const PENDING_LINKS_SELECT_ALL_MAX = 10000;

export interface PendingLinksListParams {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  sourceSurface?: string;
  sort?: string;
  sortDirection?: string;
}

export interface PendingLinkDecisionPayload {
  linkId: number | string;
  domainUuid: string;
  notes?: string;
  reasonCode?: string;
  expiresAt?: string | null;
  ownerUuid?: string;
  patternExpression?: string;
}

export interface PendingLinksBulkDecidePayload {
  decision: PendingLinkBulkDecision;
  domainUuids?: string[];
  linkIds?: Array<number | string>;
  allSelected?: boolean;
  search?: string;
  status?: string;
  sourceSurface?: string;
  notes?: string;
  reasonCode?: string;
  expiresAt?: string | null;
  ownerUuid?: string;
  patternExpression?: string;
}

export interface PendingLinksExportPayload {
  search?: string;
  status?: string;
  sourceSurface?: string;
  sort?: string;
  sortDirection?: string;
  linkIds?: Array<number | string>;
  allSelected?: boolean;
}

export function getPendingLinkDestinationUrl(link: PendingLink | null | undefined): string {
  return link?.longUrl ?? link?.requestPayload?.longUrl ?? '';
}

export function getPendingLinkRequestedBy(link: PendingLink | null | undefined): string {
  if (!link) {
    return '';
  }

  const email = link.requester?.email;
  const name = link.requester?.name;

  if (email && name && email !== name) {
    return `${name} (${email})`;
  }

  if (email) {
    return email;
  }

  if (name) {
    return name;
  }

  const submitted = link.requestPayload?.submittedBy;

  if (typeof submitted === 'string' && submitted.includes('@')) {
    return submitted;
  }

  return '';
}

export function formatPendingLinkStatus(status: string | null | undefined): string {
  if (!status) {
    return '';
  }

  const normalized = String(status).toLowerCase();

  return PENDING_LINK_STATUS_LABELS[normalized] ?? String(status).replace(/[_-]/g, ' ');
}

export function formatPendingLinkSource(source: string | null | undefined): string {
  if (!source) {
    return '';
  }

  const normalized = String(source).toLowerCase();

  return PENDING_LINK_SOURCE_LABELS[normalized] ?? String(source).replace(/[_-]/g, ' ');
}

export function mapDecisionActionToBulkDecision(
  action: PendingLinkDecisionAction
): PendingLinkBulkDecision {
  switch (action) {
    case 'approve':
      return 'approved';
    case 'approve_once':
      return 'approved_once';
    case 'reject':
      return 'rejected';
  }
}
